/**
 * Confirmation utilities for Odin Sight
 * Provides smart confirmation dialogs based on user settings
 */

import React from "react";
import { confirmDialog } from "primereact/confirmdialog";
import { useSettingsStore } from "../store/settingsStore.jsx";
import { formatPrice } from "./priceUtils.js";

// --- Styled message builder (React elements for PrimeReact confirmDialog) ---

const ROW_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "4px 0",
};
const LABEL_STYLE = { color: "#9ca3af", fontSize: "0.9rem", marginRight: 16 };
const VALUE_STYLE = {
  fontWeight: 600,
  fontSize: "0.95rem",
  textAlign: "right",
};
const HEADER_STYLE = {
  fontWeight: 700,
  fontSize: "1.05rem",
  paddingBottom: 8,
  marginBottom: 12,
  borderBottom: "1px solid rgba(150,150,150,0.25)",
};
const SEPARATOR_STYLE = {
  borderTop: "1px solid rgba(150,150,150,0.15)",
  margin: "4px 0",
};
const BUY_COLOR = "#22c55e";
const SELL_COLOR = "#ef4444";

function messageRow(label, value, key) {
  return React.createElement(
    "div",
    { key, style: ROW_STYLE },
    React.createElement("span", { style: LABEL_STYLE }, label),
    React.createElement("span", { style: VALUE_STYLE }, value),
  );
}

function messageSeparator(key) {
  return React.createElement("div", { key, style: SEPARATOR_STYLE });
}

function buildOrderMessage(order) {
  const sideLabel = order.side === "buy" ? "COMPRA" : "VENDA";
  const sideColor = order.side === "buy" ? BUY_COLOR : SELL_COLOR;
  const symbol = order.symbol || "";
  const fmt = (p) => formatPrice(symbol, p);

  const rows = [];

  // Header: side + symbol
  rows.push(
    React.createElement(
      "div",
      { key: "header", style: HEADER_STYLE },
      React.createElement("span", { style: { color: sideColor } }, sideLabel),
      " ",
      symbol,
    ),
  );

  if (order.quantity) {
    rows.push(messageRow("Quantidade", order.quantity, "qty"));
  }

  if (order.orderType === "MARKET") {
    rows.push(messageRow("Preco", "MERCADO", "price"));
  } else if (order.price != null) {
    const typeLabel = order.orderType === "STOPLIMIT" ? "(Stop)" : "(Limite)";
    rows.push(
      messageRow("Preco", `${fmt(order.price)}  ${typeLabel}`, "price"),
    );
  }

  // OTO legs
  if (order.oto && order.oto.length > 0) {
    rows.push(messageSeparator("sep-oto"));
    order.oto.forEach((leg) => {
      if (leg.id?.endsWith("-S") && leg.price != null) {
        rows.push(messageRow("Stop", fmt(leg.price), `oto-${leg.id}`));
      } else if (leg.id?.endsWith("-P") && leg.price != null) {
        rows.push(messageRow("Alvo", fmt(leg.price), `oto-${leg.id}`));
      }
    });
  }

  return React.createElement("div", { style: { minWidth: 240 } }, rows);
}

function buildModificationMessage(order, newPrice) {
  const sideLabel = order.side === "buy" ? "COMPRA" : "VENDA";
  const sideColor = order.side === "buy" ? BUY_COLOR : SELL_COLOR;
  const symbol = order.symbol || "";
  const fmt = (p) => formatPrice(symbol, p);

  const rows = [];

  rows.push(
    React.createElement(
      "div",
      { key: "header", style: HEADER_STYLE },
      React.createElement("span", { style: { color: sideColor } }, sideLabel),
      " ",
      symbol,
    ),
  );

  rows.push(messageRow("Preco atual", fmt(order.price), "old"));
  rows.push(
    messageRow(
      "Novo preco",
      React.createElement(
        "span",
        { style: { color: "#3b82f6" } },
        fmt(newPrice),
      ),
      "new",
    ),
  );

  return React.createElement("div", { style: { minWidth: 240 } }, rows);
}

function buildDeletionMessage(order) {
  const sideLabel = order.side === "buy" ? "COMPRA" : "VENDA";
  const sideColor = order.side === "buy" ? BUY_COLOR : SELL_COLOR;
  const symbol = order.symbol || "";
  const fmt = (p) => formatPrice(symbol, p);

  const rows = [];

  rows.push(
    React.createElement(
      "div",
      { key: "header", style: HEADER_STYLE },
      React.createElement("span", { style: { color: sideColor } }, sideLabel),
      " ",
      symbol,
    ),
  );

  if (order.quantity) {
    rows.push(messageRow("Quantidade", order.quantity, "qty"));
  }
  if (order.price != null) {
    rows.push(messageRow("Preco", fmt(order.price), "price"));
  }

  return React.createElement("div", { style: { minWidth: 240 } }, rows);
}

/**
 * Show confirmation dialog respecting user settings
 * If confirmOrders is disabled, executes the action directly
 *
 * @param {Object} options - Confirmation dialog options
 * @param {string} options.message - Confirmation message
 * @param {string} options.header - Dialog header
 * @param {string} options.icon - Icon to display
 * @param {string} options.acceptLabel - Accept button label
 * @param {string} options.rejectLabel - Reject button label
 * @param {Function} options.accept - Function to execute on accept
 * @param {Function} options.reject - Function to execute on reject (optional)
 */
export function showOrderConfirmation(options) {
  const { confirmOrders } = useSettingsStore.getState();

  // If confirmations are disabled, execute the action directly
  if (!confirmOrders) {
    console.log("🔄 Order confirmations disabled - executing action directly");
    if (options.accept) {
      options.accept();
    }
    return;
  }

  // Show confirmation dialog
  console.log("❓ Showing order confirmation dialog");
  confirmDialog(options);
}

/**
 * Specific confirmation dialogs for common operations
 */

export function confirmCancelOrder(orderId, onConfirm, onReject) {
  showOrderConfirmation({
    message: `Tem certeza que deseja cancelar a ordem ${orderId}?`,
    header: "Confirmar Cancelamento",
    icon: "pi pi-exclamation-triangle",
    acceptLabel: "Sim, cancelar",
    rejectLabel: "Não",
    accept: onConfirm,
    reject: onReject,
  });
}

export function confirmCancelAllOrders(onConfirm, onReject) {
  showOrderConfirmation({
    message: "Tem certeza que deseja cancelar TODAS as ordens pendentes?",
    header: "Confirmar Cancelamento",
    icon: "pi pi-exclamation-triangle",
    acceptLabel: "Sim, cancelar",
    rejectLabel: "Não",
    accept: onConfirm,
    reject: onReject,
  });
}

export function confirmCancelOrdersForSymbol(symbol, onConfirm, onReject) {
  showOrderConfirmation({
    message: `Tem certeza que deseja cancelar todas as ordens pendentes do símbolo ${symbol}?`,
    header: "Confirmar Cancelamento",
    icon: "pi pi-exclamation-triangle",
    acceptLabel: "Sim, cancelar",
    rejectLabel: "Não",
    accept: onConfirm,
    reject: onReject,
  });
}

export function confirmCloseAllPositions(onConfirm, onReject) {
  showOrderConfirmation({
    message:
      "Tem certeza que deseja fechar TODAS as posições e cancelar TODAS as ordens?",
    header: "Confirmar Fechamento Global",
    icon: "pi pi-exclamation-triangle",
    acceptLabel: "Sim, fechar todas",
    rejectLabel: "Não",
    accept: onConfirm,
    reject: onReject,
  });
}

export function confirmClosePosition(symbol, onConfirm, onReject) {
  showOrderConfirmation({
    message: `Tem certeza que deseja fechar a posição e cancelar as ordens de ${symbol}?`,
    header: "Confirmar Fechamento de Posição",
    icon: "pi pi-exclamation-triangle",
    acceptLabel: "Sim, fechar",
    rejectLabel: "Não",
    accept: onConfirm,
    reject: onReject,
  });
}

export function confirmSubmitOrder(orderDetails, onConfirm, onReject) {
  const { symbol, side, quantity, price, orderType } = orderDetails;
  const priceText = orderType === "MARKET" ? "MERCADO" : `${price}`;

  showOrderConfirmation({
    message: `Confirmar ordem: ${side.toUpperCase()} ${quantity} ${symbol} @ ${priceText}?`,
    header: "Confirmar Envio de Ordem",
    icon: "pi pi-check-circle",
    acceptLabel: "Sim, enviar",
    rejectLabel: "Cancelar",
    accept: onConfirm,
    reject: onReject,
  });
}

export function confirmModifyOrder(orderId, changes, onConfirm, onReject) {
  const changesList = [];
  if (changes.quantity) changesList.push(`quantidade: ${changes.quantity}`);
  if (changes.price) changesList.push(`preço: ${changes.price}`);

  const changesText = changesList.join(", ");

  showOrderConfirmation({
    message: `Confirmar modificação da ordem ${orderId}? (${changesText})`,
    header: "Confirmar Modificação",
    icon: "pi pi-pencil",
    acceptLabel: "Sim, modificar",
    rejectLabel: "Cancelar",
    accept: onConfirm,
    reject: onReject,
  });
}

/**
 * Promise-based confirmation wrapper.
 * If confirmOrders is OFF, resolves true immediately.
 * If ON, shows dialog and resolves true (accept) or false (reject).
 *
 * @param {Object} options - confirmDialog options (message, header, icon, etc.)
 * @returns {Promise<boolean>}
 */
export function showOrderConfirmationAsync(options) {
  const { confirmOrders } = useSettingsStore.getState();
  if (!confirmOrders) return Promise.resolve(true);

  return new Promise((resolve) => {
    confirmDialog({
      ...options,
      accept: () => resolve(true),
      reject: () => resolve(false),
    });
  });
}

/**
 * Confirm a new order submission (chart click or double-click submit).
 * @param {Object} order - Order data
 * @returns {Promise<boolean>}
 */
export function confirmNewOrder(order) {
  return showOrderConfirmationAsync({
    message: buildOrderMessage(order),
    header: "Confirmar Envio de Ordem",
    icon: "pi pi-check-circle",
    acceptLabel: "Enviar",
    rejectLabel: "Cancelar",
  });
}

/**
 * Confirm an order price modification (drag on open order).
 * @param {Object} order - The order being modified
 * @param {number} newPrice - The proposed new price
 * @returns {Promise<boolean>}
 */
export function confirmOrderModification(order, newPrice) {
  return showOrderConfirmationAsync({
    message: buildModificationMessage(order, newPrice),
    header: "Confirmar Modificacao",
    icon: "pi pi-pencil",
    acceptLabel: "Modificar",
    rejectLabel: "Cancelar",
  });
}

/**
 * Confirm cancellation of an open/pending order from the chart.
 * @param {Object} order - The order to cancel
 * @returns {Promise<boolean>}
 */
export function confirmOrderDeletion(order) {
  return showOrderConfirmationAsync({
    message: buildDeletionMessage(order),
    header: "Confirmar Cancelamento",
    icon: "pi pi-exclamation-triangle",
    acceptLabel: "Cancelar Ordem",
    rejectLabel: "Manter",
  });
}

export default showOrderConfirmation;
